<template>
  <div class="dashboard-page">
    <!-- ── Header ── -->
    <div class="dash-header">
      <h2 class="welcome-title">Welcome back, {{ displayUsername }}!</h2>
      <div class="header-divider"></div>
      <div class="secure-badge">
        <i class="fas fa-check-circle secure-icon"></i>
        <span>Secure · Registered Fintech</span>
      </div>
    </div>

    <!-- ── Body ── -->
    <div class="dash-body" v-if="!isInitialLoading">
      <!-- Account Summary -->
      <div class="dash-card card-elevated">
        <p class="card-label">Account Summary</p>
        <div class="summary-row summary-total">
          <span class="row-key">Principal Account</span>
          <span class="row-val"><strong>{{ formatAmount(totalBalance) }}</strong> <span class="cur">FCFA</span></span>
        </div>
        <div class="row-divider"></div>

        <!-- 1st campaign -->
        <div class="summary-row">
          <span class="row-key">
            {{ firstCampaignSummary.name }}
            <span class="campaign-mini">{{ firstCampaignSummary.info }}</span>
          </span>
          <span class="row-val amount-display">
            <template v-if="firstCampaignSummary.hasAmount">
              <strong>{{ formatAmount(firstCampaignSummary.current) }}</strong>
              <span class="cur">FCFA</span>
              <span class="cur">/</span>
              <strong>{{ formatAmount(firstCampaignSummary.target) }}</strong>
              <span class="cur">FCFA</span>
            </template>
            <template v-else-if="firstCampaignSummary.hasDate">
              <strong>{{ formatAmount(firstCampaignSummary.current) }}</strong>
              <span class="cur">FCFA</span>
              <span class="cur">·</span>
              <span class="cur">{{ firstCampaignSummary.targetDate }}</span>
            </template>
            <template v-else>
              <strong>{{ formatAmount(firstCampaignSummary.current) }}</strong>
              <span class="cur">FCFA</span>
            </template>
          </span>
        </div>

        <!-- 2nd campaign -->
        <div v-if="secondCampaignSummary.exists" class="row-divider"></div>
        <div v-if="secondCampaignSummary.exists" class="summary-row summary-available">
          <span class="row-key">
            {{ secondCampaignSummary.name }}
            <span class="campaign-mini">{{ secondCampaignSummary.info }}</span>
          </span>
          <span class="row-val available-val amount-display">
            <template v-if="secondCampaignSummary.hasAmount">
              <strong>{{ formatAmount(secondCampaignSummary.current) }}</strong>
              <span class="cur">FCFA</span>
              <span class="cur">/</span>
              <strong>{{ formatAmount(secondCampaignSummary.target) }}</strong>
              <span class="cur">FCFA</span>
            </template>
            <template v-else-if="secondCampaignSummary.hasDate">
              <strong>{{ formatAmount(secondCampaignSummary.current) }}</strong>
              <span class="cur">FCFA</span>
              <span class="cur">·</span>
              <span class="cur">{{ secondCampaignSummary.targetDate }}</span>
            </template>
            <template v-else>
              <strong>{{ formatAmount(secondCampaignSummary.current) }}</strong>
              <span class="cur">FCFA</span>
            </template>
          </span>
        </div>
      </div>

      <!-- Your Savings Goal -->
      <div class="dash-card">
        <p class="card-label">Your Savings Goal</p>
        <div class="goal-header">
          <div>
            <p class="goal-name">{{ currentGoal.name }}</p>
            <p class="goal-amounts amount-display">
              <template v-if="currentGoal.hasAmount">
                {{ formatAmount(currentGoal.current) }} FCFA /
                {{ formatAmount(currentGoal.target) }} FCFA
              </template>
              <template v-else-if="currentGoal.hasDate">
                {{ formatAmount(currentGoal.current) }} FCFA · {{ currentGoal.targetDate }}
              </template>
              <template v-else>
                {{ formatAmount(currentGoal.current) }} FCFA saved
              </template>
            </p>
          </div>
          <span class="goal-pct" :class="{ 'goal-pct--text': !goalProgressDisplay.isPercent }">
            {{ goalProgressDisplay.primaryText }}
            <span v-if="goalProgressDisplay.hasSecondary" class="goal-pct-secondary">
              {{ goalProgressDisplay.secondaryText }}
            </span>
          </span>
        </div>

        <div class="progress-track">
          <div class="progress-fill" :style="{ width: goalPercent + '%' }"></div>
        </div>

        <button class="add-money-btn" @click="addMoney">
          {{ addMoneyButtonText }}
        </button>

        <div class="link-row" @click="viewAllGoals">
          View All Goals <i class="fas fa-chevron-right link-chevron"></i>
        </div>
      </div>

      <!-- Quick Actions -->
      <div class="dash-card">
        <p class="card-label">Quick Actions</p>
        <div class="actions-grid">
<div
            class="action-item"
            v-for="action in computedQuickActions"
            :key="action.label"
            :class="{ 'action-item--loan': action.label === 'Quick Loan' }"
            @click="action.label === 'Refer a Client' ? goToReferrals() : navigate(action.link)"
          >
            <div class="action-icon-wrap">
              <img
                v-if="action.label === 'Quick Loan'"
                class="loan-img"
                :src="loanIcon"
                alt="Quick Loan"
              />
              <i v-else-if="action.label === 'Marketplace'" class="mdi mdi-cart-variant action-icon-big"></i>
              <i v-else :class="[action.faIcon, 'action-icon-big']"></i>
            </div>
            <span class="action-label">{{ action.label }}</span>
          </div>
        </div>
      </div>

      <!-- Recent Activity -->
      <div class="dash-card activity-card">
        <p class="card-label">Recent Activity</p>
        <div
          class="activity-row"
          v-for="(item, i) in recentActivity"
          :key="i"
        >
          <span class="activity-amount" :class="item.type === 'credit' ? 'credit' : 'debit'">
            {{ item.type === 'credit' ? '+' : '-' }}
            <strong>{{ formatAmountComma(item.amount) }}</strong>
            <span class="cur-sm">FCFA</span>
          </span>
          <span class="activity-desc">{{ item.description }}</span>
        </div>

        <div class="link-row mt-2" @click="viewAllActivity">
          View All <i class="fas fa-chevron-right link-chevron"></i>
        </div>
      </div>
    </div>

    <!-- État de chargement initial -->
    <div v-else class="loading-container">
      <div class="loading-spinner"></div>
      <p>Loading your dashboard...</p>
    </div>

    <!-- ── Campaign Picker Dialog ── -->
    <div v-if="showCampaignDialog" class="campaign-modal" @click.self="closeCampaignDialog">
      <div class="campaign-modal-card">
        <div class="campaign-modal-header">
          <h3>Select Campaign</h3>
          <button class="campaign-close" @click="closeCampaignDialog">x</button>
        </div>

        <div v-if="selectableCampaigns.length" class="campaign-list">
          <button
            v-for="campaign in selectableCampaigns"
            :key="campaign.id || campaign.campaign_id"
            class="campaign-option"
            @click="goToCampaign(campaign)"
          >
            <div class="campaign-main">
              <span class="campaign-name">{{ formatCampaignName(campaign) }}</span>
              <span class="campaign-sub">Creation Date: {{ campaign.get_date_str || '-' }}</span>
              <span class="campaign-sub">
                Objective:
                <template v-if="campaign.target_amount && campaign.target_date">
                  Reach {{ formatAmount(campaign.target_amount) }} FCFA by {{ campaign.target_date }}
                </template>
                <template v-else-if="campaign.target_amount">
                  Reach {{ formatAmount(campaign.target_amount) }} FCFA
                </template>
                <template v-else-if="campaign.target_date">
                  On {{ campaign.target_date }}
                </template>
                <template v-else>
                  Not defined
                </template>
              </span>
            </div>
            <span class="campaign-sub amount-display">
              {{ formatAmount(getCampaignSaved(campaign)) }} FCFA
            </span>
          </button>
        </div>
        <div v-else class="campaign-empty">
          No active campaigns found.
        </div>

        <div class="campaign-actions">
          <button class="campaign-cancel" @click="closeCampaignDialog">Cancel</button>
        </div>
      </div>
    </div>

    <!-- ── Footer ── -->
    <div class="footer-divider"></div>
    <div class="dash-footer">
      <i class="mdi mdi-lock footer-lock"></i>
      <span>Safe &amp; Secure</span>
      <span class="footer-sep">·</span>
      <i class="fas fa-map-marker-alt footer-pin"></i>
      <span>Based in Technipole ENSP</span>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import loanIcon from "@/assets/Group 5.svg";

export default {
  name: "Dashboard",

  data() {
    return {
      username: "",
      totalBalance: 0,
      walletBalance: 0,

      // 🔁 Initialisé à null pour distinguer "chargement en cours" de "aucune donnée"
      campaigns: null,
      showCampaignDialog: false,
      loanIcon,

      // Verrou pour éviter les appels API concurrents
      isLoading: false,

      quickActions: [
        { label: "Investments",    faIcon: "fas fa-plus",        link: "/savings/investments"          },
        { label: "My Njangi",      faIcon: "fas fa-users",       link: "/njangi/groups/types/Active"   },
        { label: "Quick Loan",     faIcon: "",                   link: "/savings/loans/active"         },
        { label: "Refer a Client", faIcon: "fas fa-paper-plane", link: "/become_partner"  },
      ],

      recentActivity: [],
    };
  },

  computed: {
    // Indique si le chargement initial est en cours
    isInitialLoading() {
      return this.campaigns === null;
    },

    displayUsername() {
      return (
        this.$store?.state?.user_info?.user?.username ||
        this.$store?.state?.username ||
        this.username
      );
    },

    computedQuickActions() {
      const eligible = this.$store.state.isMarketer || (this.$store.state.businessAccount && this.$store.state.paymentStatus);
      return this.quickActions.map(a =>
        a.label === "Refer a Client"
          ? { ...a, link: eligible ? "/savings/referrals/dashboard" : "/become_partner" }
          : a
      );
    },

    // ── Filtres de campagnes ──────────────────────────────────────────────────

    allSavingsCampaigns() {
      // Si campaigns est null (chargement), on retourne un tableau vide pour éviter les erreurs
      if (!this.campaigns) return [];
      return this.campaigns.filter(
        (c) => !this.isPrincipalCampaign(c) && !this.isMarketerCampaign(c)
      );
    },

    activeSavingsCampaigns() {
      return this.allSavingsCampaigns.filter((c) => !this.isCompletedCampaign(c));
    },

    hasActiveCampaign() {
      return this.activeSavingsCampaigns.length > 0;
    },

    hasAnySavingsCampaign() {
      return this.allSavingsCampaigns.length > 0;
    },

    selectableCampaigns() {
      return this.activeSavingsCampaigns.filter((c) => !this.isCompletedCampaign(c));
    },

    // ── currentGoal : COMPUTED (dérivé de campaigns) ─────────────────────────
    currentGoal() {
      // Si en cours de chargement, affichage temporaire
      if (this.campaigns === null) {
        return {
          name: "Loading...",
          current: 0,
          target: 0,
          hasAmount: false,
          hasDate: false,
          targetDate: null,
          passedDays: 0,
          remainingDays: 0,
        };
      }

      const firstActive = this.activeSavingsCampaigns[0];

      if (!firstActive) {
        return {
          name: "No active goal",
          current: 0,
          target: 0,
          hasAmount: false,
          hasDate: false,
          targetDate: null,
          passedDays: 0,
          remainingDays: 0,
        };
      }

      const type      = this.getCampaignType(firstActive);
      const hasAmount = type === "MONEY_TARGET" || type === "MONEY_TIME_TARGET";
      const hasDate   = type === "TIME_TARGET"  || type === "MONEY_TIME_TARGET";
      const { remainingDays, passedDays } = this.calculateDaysFromCampaign(firstActive);

      return {
        name:         firstActive.name || "Savings Goal",
        current:      Number(firstActive.saved_amount || firstActive.balance || firstActive.current_balance || 0),
        target:       Number(firstActive.target_amount || 0),
        hasAmount,
        hasDate,
        targetDate:   firstActive.target_date || null,
        passedDays,
        remainingDays,
      };
    },

    // ── Résumés des campagnes (Summary card) ─────────────────────────────────

    firstCampaignSummary() {
      return this._buildCampaignSummary(this.activeSavingsCampaigns[0], "1st Campaign");
    },

    secondCampaignSummary() {
      const summary = this._buildCampaignSummary(this.activeSavingsCampaigns[1], "2nd Campaign");
      summary.exists = !!this.activeSavingsCampaigns[1];
      return summary;
    },

    // ── Progression de l'objectif ─────────────────────────────────────────────

    goalPercent() {
      const g = this.currentGoal;

      if (g.hasAmount && g.target > 0) {
        return Math.min(Math.round((g.current / g.target) * 100), 100);
      }

      if (g.hasDate) {
        const passed    = Number(g.passedDays    || 0);
        const remaining = Number(g.remainingDays || 0);
        const total     = passed + remaining;
        if (total > 0) return Math.min(Math.round((passed / total) * 100), 100);
        return 0;
      }

      return 0;
    },

    goalProgressDisplay() {
      const g = this.currentGoal;

      if (g.hasAmount) {
        const primary = `${this.goalPercent}%`;
        if (g.hasDate) {
          const remaining  = Number(g.remainingDays) || 0;
          const secondary  = remaining > 0 ? `${remaining} days left` : "Date reached";
          return { primaryText: primary, secondaryText: secondary, isPercent: true, hasSecondary: true };
        }
        return { primaryText: primary, secondaryText: null, isPercent: true, hasSecondary: false };
      }

      if (g.hasDate) {
        const remaining = Number(g.remainingDays) || 0;
        const text      = remaining > 0 ? `${remaining} days left` : "Date reached";
        return { primaryText: text, secondaryText: null, isPercent: false, hasSecondary: false };
      }

      return { primaryText: "—", secondaryText: null, isPercent: false, hasSecondary: false };
    },

    // ── Texte dynamique du bouton ─────────────────────────────────────────────

    addMoneyButtonText() {
      if (this.hasActiveCampaign)     return "Add Money";
      if (this.hasAnySavingsCampaign) return "Create new campaign";
      return "Create first campaign";
    },
  },

  async mounted() {
    document.title = "PettyCash | Dashboard";
    this.hydrateFromStore();
    await this.fetchDashboardData();
  },

  methods: {
    hydrateFromStore() {
      const storeUser = this.$store?.state?.user_info?.user;
      if (storeUser?.username) this.username = storeUser.username;
    },

    // ── Helpers campagne ──────────────────────────────────────────────────────

    getCampaignType(campaign) {
      const hasAmount = !!campaign?.target_amount;
      const hasDate   = !!campaign?.target_date;
      if (hasAmount && hasDate) return "MONEY_TIME_TARGET";
      if (hasAmount)            return "MONEY_TARGET";
      if (hasDate)              return "TIME_TARGET";
      return "UNDEFINED";
    },

    calculateDaysFromCampaign(campaign) {
      let remainingDays = Number(campaign?.remaining_days || 0);
      let passedDays    = Number(campaign?.passed_days    || 0);

      if (campaign?.target_date) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const targetDate = new Date(campaign.target_date);
        targetDate.setHours(0, 0, 0, 0);

        const diffDays = Math.ceil((targetDate - today) / (1000 * 60 * 60 * 24));

        if (!campaign.remaining_days || remainingDays !== diffDays) {
          remainingDays = diffDays > 0 ? diffDays : 0;
        }

        if (campaign.creation_date) {
          const creationDate = new Date(campaign.creation_date);
          creationDate.setHours(0, 0, 0, 0);
          const passed = Math.floor((today - creationDate) / (1000 * 60 * 60 * 24));
          passedDays = passed > 0 ? passed : 0;
        }
      }

      return { remainingDays, passedDays };
    },

    _buildCampaignSummary(campaign, fallbackName) {
      if (!campaign) {
        return {
          name: fallbackName, current: 0, target: 0,
          info: "No active campaign",
          hasAmount: false, hasDate: false, targetDate: null,
          passedDays: 0, remainingDays: 0, exists: false,
        };
      }

      const type      = this.getCampaignType(campaign);
      const current   = Number(campaign.saved_amount || campaign.balance || campaign.current_balance || 0);
      const target    = Number(campaign.target_amount || 0);
      const hasAmount = type === "MONEY_TARGET" || type === "MONEY_TIME_TARGET";
      const hasDate   = type === "TIME_TARGET"  || type === "MONEY_TIME_TARGET";
      const { remainingDays, passedDays } = this.calculateDaysFromCampaign(campaign);

      let info = "";
      if (hasAmount && target > 0) {
        const pct = Math.min(Math.round((current / target) * 100), 100);
        info = `${pct}% reached`;
      } else if (hasDate) {
        info = remainingDays > 0 ? `${remainingDays} days left` : "Date reached";
      } else {
        info = "No target set";
      }

      return {
        name: campaign.name || fallbackName,
        current,
        target,
        hasAmount,
        hasDate,
        targetDate:   campaign.target_date || null,
        passedDays,
        remainingDays,
        info,
        exists: true,
      };
    },

    // ── Fetch principal (avec protection contre les appels multiples et les réponses vides) ──

    async fetchDashboardData() {
      // Évite les appels concurrents
      if (this.isLoading) return;
      this.isLoading = true;

      try {
        const requests = [
          axios.get("/api/v1/savings/wallet/balances/"),
          axios.get("/api/v1/savings/get/user/campaigns/all/"),
          axios.get("/api/v1/savings/get/user/transactions/null/null/all/"),
        ];

        const needsUserInfo =
          !this.$store?.state?.user_info?.user?.username &&
          !this.$store?.state?.username;

        if (needsUserInfo) requests.push(axios.get("/api/v1/home/get/user/info/"));

        const results = await Promise.allSettled(requests);

        // ── Balances ────────────────────────────────────────────────────────────
        const balancesResult = results[0];
        let fallbackPrincipalSaved = null;
        if (balancesResult.status === "fulfilled") {
          const b = balancesResult.value.data || {};
          fallbackPrincipalSaved = Number(b.principal_campaign?.balance || 0);
          this.walletBalance = Number(b.wallet_balance ?? b.wallet?.balance ?? b.balance ?? 0);
        }

        // ── Campaigns ───────────────────────────────────────────────────────────
        const campaignsResult = results[1];
        if (campaignsResult.status === "fulfilled") {
          const raw = campaignsResult.value.data || [];
          
          // ✅ PROTECTION : on ne met à jour campaigns QUE si on reçoit un tableau non vide
          //    ou si c'est le premier chargement (campaigns === null)
          if (Array.isArray(raw) && (raw.length > 0 || this.campaigns === null)) {
            this.$set(this, "campaigns", raw);
          } else if (Array.isArray(raw) && raw.length === 0 && this.campaigns !== null) {
            // Si on reçoit un tableau vide alors qu'on a déjà des données, on conserve l'état précédent
            console.warn("Réponse vide pour les campagnes, conservation de l'état précédent.");
          }

          // Solde principal (lu depuis les campagnes)
          const principalCampaign   = (this.campaigns || []).find((c) => this.isPrincipalCampaign(c));
          const principalSaved      = Number(
            principalCampaign?.saved_amount ??
            principalCampaign?.balance      ??
            fallbackPrincipalSaved          ??
            0
          );
          this.totalBalance = principalSaved;
        } else {
          // En cas d'échec, on ne touche pas à campaigns sauf si premier chargement
          if (this.campaigns === null) {
            this.$set(this, "campaigns", []);
          }
          if (fallbackPrincipalSaved !== null) {
            this.totalBalance = fallbackPrincipalSaved;
          }
        }

        // ── Transactions ────────────────────────────────────────────────────────
        const activityResult = results[2];
        if (activityResult.status === "fulfilled") {
          const transactions = activityResult.value.data || [];
          if (Array.isArray(transactions) && transactions.length > 0) {
            this.recentActivity = transactions.slice(0, 3).map((t) => {
              const isWithdrawal      = Boolean(t.withdrawal);
              const typeHint          = String(t.type || t.transaction_type || "").toLowerCase();
              const isDeposit         = typeHint.includes("deposit") || !isWithdrawal;
              const rawDesc           = String(t.description || "").trim();
              const isGenericDeposit  = rawDesc.toLowerCase() === "deposit" || rawDesc === "";
              const description       = isWithdrawal
                ? (rawDesc || "Withdrawal")
                : (isGenericDeposit || isDeposit ? "Deposit Successful" : rawDesc);
              return {
                type:   isWithdrawal ? "debit" : "credit",
                amount: Number(t.amount || 0),
                description,
              };
            });
          } else {
            this.recentActivity = [];
          }
        }

        // ── User info ───────────────────────────────────────────────────────────
        if (needsUserInfo) {
          const userInfoResult = results[3];
          if (userInfoResult?.status === "fulfilled") {
            const data  = userInfoResult.value.data;
            const first = Array.isArray(data) ? data[0] : data;
            const user  = first?.user;
            if (user?.username) this.username = user.username;
          }
        }
      } catch (error) {
        console.error("Erreur lors du chargement du dashboard :", error);
        if (this.campaigns === null) {
          this.$set(this, "campaigns", []);
        }
      } finally {
        this.isLoading = false;
      }
    },

    // ── Formatage ─────────────────────────────────────────────────────────────

    formatAmount(val)      { return Number(val).toLocaleString("en-US"); },
    formatAmountComma(val) { return Number(val).toLocaleString("en-US"); },

    // ── Navigation ────────────────────────────────────────────────────────────

    addMoney() {
      if (this.hasActiveCampaign) {
        this.showCampaignDialog = true;
      } else {
        this.$router.push("/savings/campaigns/create/new_campaign");
      }
    },

    viewAllGoals()        { this.$router.push("/savings/campaigns/active"); },
    viewAllActivity()     { this.$router.push("/activity");                 },
    navigate(link)        { this.$router.push(link);                        },

    async goToReferrals() {
      const eligible = await this.$store.dispatch('loadPartnerEligibility');
      this.$router.push(eligible ? '/savings/referrals/dashboard' : '/become_partner');
    },
    closeCampaignDialog() { this.showCampaignDialog = false;                },

    goToCampaign(campaign) {
      const id = campaign?.id ?? campaign?.campaign_id ?? campaign?.pk;
      if (!id) return;
      this.showCampaignDialog = false;
      this.$router.push(`/savings/campaigns/save/${id}`);
    },

    formatCampaignName(campaign) {
      if (this.isPrincipalCampaign(campaign)) return "Principal Account";
      return campaign?.name || "Savings Campaign";
    },

    getCampaignSaved(campaign) {
      return Number(
        campaign?.saved_amount    ??
        campaign?.balance         ??
        campaign?.current_balance ??
        0
      );
    },

    // Détection plus robuste des campagnes principales et marketeurs
    isPrincipalCampaign(c) {
      return Boolean(
        c?.principal || c?.is_principal ||
        c?.is_principal_campaign || c?.is_principal_account ||
        c?.campaign_type === 'principal' || c?.type === 'principal'
      );
    },

    isMarketerCampaign(c) {
      return Boolean(
        c?.marketer || c?.is_marketer || c?.marketer_campaign ||
        c?.campaign_type === 'marketer' || c?.type === 'marketer'
      );
    },

    isCompletedCampaign(c) {
      if (c?.completed === true || c?.is_completed === true) return true;
      const s = String(
        c?.status || c?.campaign_status || c?.state || c?.campaign_state || ""
      ).toLowerCase();
      return (
        s.includes("completed") || s.includes("complete") ||
        s.includes("done")      || s.includes("closed")   ||
        s.includes("finished")
      );
    },
  },
};
</script>

<style scoped>
/* Inclusion de la police */
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700&family=Roboto:wght@400;500;700;900&display=swap');

/* Styles globaux dans le scope */
.dashboard-page {
  min-height: 100vh;
  background: #f8fafc;
  font-family: 'Roboto', sans-serif;
  max-width: 480px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
}

.dashboard-page * {
  font-family: 'Roboto', sans-serif;
}

/* Header */
.dash-header {
  background: linear-gradient(180deg, #f0f7ff 0%, #f8fafc 100%);
  padding: 18px 16px 12px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  border-bottom: 1px solid rgba(30, 58, 138, 0.08);
}
.welcome-title {
  font-size: 18px;
  font-weight: 800;
  color: #0f172a;
  margin: 0;
  letter-spacing: -0.2px;
}
.header-divider {
  height: 3px;
  background: linear-gradient(90deg, #d97706 0%, #f59e0b 100%);
  width: 120px;
  margin: 8px 0 6px;
  border-radius: 2px;
}
.secure-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: #64748b;
  font-weight: 500;
  background: rgba(255,255,255,0.6);
  padding: 4px 10px;
  border-radius: 20px;
  border: 1px solid rgba(30, 58, 138, 0.1);
}
.secure-icon { color: #1e40af; font-size: 12px; }

/* Body */
.dash-body {
  flex: 0 0 auto;
  padding: 14px 16px 10px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* Cartes */
.dash-card {
  background: #ffffff;
  border-radius: 12px;
  padding: 16px;
  box-shadow:
    0 4px 6px -1px rgba(0,0,0,0.02),
    0 2px 4px -2px rgba(0,0,0,0.02),
    0 10px 20px -3px rgba(30, 58, 138, 0.06);
  border: 1px solid #e2e8f0;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}
.dash-card:hover {
  transform: translateY(-2px);
  box-shadow:
    0 10px 15px -3px rgba(0,0,0,0.05),
    0 4px 6px -4px rgba(0,0,0,0.03),
    0 20px 25px -5px rgba(30, 58, 138, 0.08);
}
.card-label {
  font-size: 12px;
  font-weight: 700;
  color: #0f172a;
  text-transform: none;
  letter-spacing: 0.2px;
  margin: -16px -16px 12px;
  padding: 10px 14px;
  background: linear-gradient(90deg, #f8fafc 0%, #f1f5f9 100%);
  border-bottom: 1px solid #e2e8f0;
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
  font-family: 'Roboto', sans-serif;
}

/* Summary rows */
.summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
}
.summary-row:first-of-type {
  padding: 8px 0 10px;
}
.summary-row:first-of-type .row-key {
  font-size: 13px;
  color: #64748b;
  font-weight: 600;
}
.summary-row:first-of-type .row-val strong {
  font-size: 24px;
  font-weight: 700;
  color: #0f172a;
  font-family: 'Playfair Display', serif;
  letter-spacing: -0.5px;
}
.summary-row:first-of-type .row-val .cur {
  font-size: 14px;
  color: #94a3b8;
  font-weight: 600;
  margin-left: 4px;
}
.summary-row:nth-of-type(2) .row-key { font-size: 13px; color: #64748b; }
.summary-row:nth-of-type(2) .row-val strong { font-size: 15px; font-weight: 600; color: #334155; }
.summary-row:last-of-type { opacity: 0.85; }
.summary-row:last-of-type .row-key { font-size: 12px; color: #94a3b8; font-weight: 500; }
.summary-row:last-of-type .row-val strong { font-size: 14px; font-weight: 500; color: #64748b; }
.summary-row:last-of-type .row-val .cur { font-size: 12px; color: #94a3b8; }

.row-key { font-size: 13px; color: #64748b; }
.amount-display strong { font-family: 'Playfair Display', serif; letter-spacing: -0.2px; }
.campaign-mini {
  display: block;
  font-size: 10px;
  color: #94a3b8;
  margin-top: 2px;
  font-weight: 500;
}
.row-val {
  font-size: 14px;
  color: #334155;
  display: inline-flex;
  align-items: baseline;
  gap: 3px;
}
.row-val strong { font-size: 15px; font-weight: 600; }
.available-val  { align-items: center; gap: 4px; }
.cur            { font-size: 11px; font-weight: 500; color: #94a3b8; }
.info-icon      { font-size: 12px; color: #94a3b8; }
.row-divider    { height: 1px; background: linear-gradient(90deg, transparent, #e2e8f0, transparent); margin: 2px 0; }

/* Goal */
.goal-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
}
.goal-name    { font-size: 15px; font-weight: 700; color: #0f172a; margin: 0 0 4px; }
.goal-amounts { font-size: 12px; color: #94a3b8; margin: 0; font-weight: 500; }
.goal-pct {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  font-size: 20px;
  font-weight: 800;
  color: #1e40af;
  font-family: 'Playfair Display', serif;
}
.goal-pct--text {
  font-size: 14px;
  font-weight: 600;
  color: #1e40af;
  white-space: nowrap;
  justify-content: center;
}
.goal-pct-secondary {
  font-size: 11px;
  font-weight: 500;
  color: #64748b;
  font-family: 'Roboto', sans-serif;
  margin-top: 4px;
  white-space: nowrap;
  line-height: 1.2;
}

/* Progress */
.progress-track {
  width: 100%;
  height: 8px;
  background: #e0f2fe;
  border-radius: 99px;
  overflow: hidden;
  margin-bottom: 14px;
  position: relative;
  box-shadow: inset 0 1px 2px rgba(0,0,0,0.05);
}
.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #06b6d4 0%, #3b82f6 50%, #1e40af 100%);
  border-radius: 99px;
  transition: width 1s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}
.progress-fill::after {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 50%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent);
  animation: shimmer 2.5s infinite;
}
@keyframes shimmer {
  0%   { left: -100%; }
  100% { left: 200%; }
}

/* Bouton Add Money */
.add-money-btn {
  width: 60%;
  margin: 0 auto;
  display: block;
  padding: 12px 0;
  background: linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%);
  color: #fff;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  letter-spacing: 0.5px;
  box-shadow: 0 4px 12px rgba(30, 58, 138, 0.25);
  transition: all 0.3s ease;
  font-family: inherit;
  position: relative;
  overflow: hidden;
}
.add-money-btn::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 60%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.25), transparent);
  transform: skewX(-20deg);
  transition: left 0.7s;
}
.add-money-btn:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(30, 58, 138, 0.35); }
.add-money-btn:hover::before { left: 150%; }
.add-money-btn:active { transform: translateY(0); }

/* Liens */
.link-row {
  text-align: center;
  margin-top: 12px;
  color: #1e40af;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: all 0.2s;
  padding: 4px;
  border-radius: 6px;
}
.link-row:hover { color: #1e3a8a; background: rgba(30, 58, 138, 0.04); }
.link-chevron   { font-size: 10px; transition: transform 0.2s; }
.link-row:hover .link-chevron { transform: translateX(3px); }

/* Quick Actions */
.actions-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
}
.action-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 10px 6px;
}
.action-item:hover {
  transform: translateY(-4px);
  background: #fff;
  box-shadow: 0 10px 20px -5px rgba(30, 58, 138, 0.1);
  border-color: #cbd5e1;
}
.action-icon-wrap {
  width: 54px;
  height: 54px;
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  overflow: visible;
  border: 1px solid rgba(30, 58, 138, 0.08);
}
.action-item:hover .action-icon-wrap {
  background: linear-gradient(135deg, #1e40af 0%, #3b82f6 100%);
  box-shadow: 0 8px 16px -4px rgba(30, 58, 138, 0.3);
}
.action-item:hover .action-icon-big { color: white; }
.action-icon-big { font-size: 26px; color: rgba(0, 0, 0, 0.54); transition: color 0.3s; }
.loan-img        { width: 44px; height: 44px; object-fit: contain; display: block; transition: transform 0.3s; }
.action-item:hover .loan-img { transform: scale(1.1); }
.action-label {
  font-size: 10px;
  font-weight: 600;
  color: #64748b;
  text-align: center;
  line-height: 1.3;
  transition: color 0.3s;
}
.action-item:hover .action-label { color: #0f172a; }

/* Activité récente */
.activity-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 0;
  border-bottom: 1px solid #f1f5f9;
  transition: background 0.2s;
  margin: 0 -4px;
  padding-left: 4px;
  padding-right: 4px;
  border-radius: 6px;
}
.activity-row:hover { background: #f8fafc; }
.activity-row:last-of-type { border-bottom: none; }
.activity-amount {
  font-size: 14px;
  display: inline-flex;
  align-items: baseline;
  gap: 4px;
  min-width: 110px;
}
.activity-amount strong { font-size: 15px; font-family: 'Playfair Display', serif; }
.credit        { color: #1e40af; }
.credit strong { color: #1e40af; }
.debit         { color: #dc2626; }
.cur-sm        { font-size: 11px; color: #94a3b8; font-weight: 500; }
.activity-amount .cur-sm { color: inherit; opacity: 0.8; }
.activity-desc { font-size: 13px; color: #64748b; flex: 1; font-weight: 500; }
.mt-2          { margin-top: 10px; }

/* Modal */
.campaign-modal {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  backdrop-filter: blur(3px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
  padding: 16px;
}
.campaign-modal-card {
  width: 100%;
  max-width: 380px;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 24px 50px rgba(15, 23, 42, 0.25);
  border: 1px solid #e2e8f0;
  padding: 16px;
  animation: modalIn 0.22s ease;
}
.campaign-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}
.campaign-modal-header h3 { margin: 0; font-size: 15px; font-weight: 700; color: #0f172a; }
.campaign-close {
  width: 28px; height: 28px;
  border-radius: 50%; border: none;
  background: #f1f5f9; font-size: 16px; line-height: 1;
  cursor: pointer; color: #64748b;
  display: flex; align-items: center; justify-content: center;
  transition: background 0.2s ease, color 0.2s ease;
}
.campaign-close:hover { background: #e2e8f0; color: #0f172a; }
.campaign-list {
  display: flex; flex-direction: column; gap: 10px;
  max-height: 280px; overflow: auto; padding-right: 4px;
}
.campaign-option {
  display: flex; align-items: flex-start; justify-content: space-between;
  gap: 10px; width: 100%;
  border: 1px solid #e2e8f0; background: #f8fafc;
  border-radius: 12px; padding: 12px 14px;
  cursor: pointer; transition: all 0.2s ease; text-align: left;
}
.campaign-option:hover {
  background: #eef2ff; border-color: #c7d2fe;
  transform: translateY(-1px);
  box-shadow: 0 8px 16px rgba(15, 23, 42, 0.08);
}
.campaign-main { display: flex; flex-direction: column; gap: 4px; flex: 1; }
.campaign-name { font-size: 13px; font-weight: 600; color: #0f172a; }
.campaign-sub  { font-size: 12px; color: #64748b; white-space: nowrap; }
.campaign-empty { font-size: 12px; color: #64748b; text-align: center; padding: 10px 0 4px; }
.campaign-actions { display: flex; gap: 8px; margin-top: 14px; }
.campaign-cancel, .campaign-view {
  flex: 1; border: none; border-radius: 10px;
  padding: 10px 12px; font-size: 12px; font-weight: 600; cursor: pointer;
}
.campaign-cancel { background: #f1f5f9; color: #475569; }
.campaign-view   { background: linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%); color: #fff; box-shadow: 0 8px 18px rgba(30, 58, 138, 0.25); }
@keyframes modalIn {
  from { transform: scale(0.96); opacity: 0; }
  to   { transform: scale(1); opacity: 1; }
}

/* Footer */
.footer-divider { height: 1px; background: linear-gradient(90deg, transparent, #cbd5e1, transparent); margin: 16px 16px 8px; }
.dash-footer {
  display: flex; align-items: center; justify-content: center;
  gap: 8px; padding: 12px 0 16px;
  font-size: 11px; color: #94a3b8; font-weight: 500;
  flex-wrap: wrap; text-align: center; margin-top: auto;
}
.footer-lock { color: #1e40af; font-size: 13px; }
.footer-pin  { color: #dc2626; font-size: 13px; }
.footer-sep  { color: #cbd5e1; }

/* Loading state */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  color: #64748b;
  font-size: 14px;
  gap: 16px;
}
.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #e2e8f0;
  border-top-color: #1e40af;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}
@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Responsive */
@media (max-width: 380px) {
  .welcome-title    { font-size: 16px; }
  .action-icon-wrap { width: 46px; height: 46px; }
  .action-icon-big  { font-size: 22px; }
  .loan-img         { width: 36px; height: 36px; }
  .action-label     { font-size: 9px; }
  .summary-row:first-of-type .row-val strong { font-size: 20px; }
}

@media (max-width: 599px) {
  .dash-header { padding: 14px 14px 10px; }
  .header-divider { margin: 6px 0 4px; width: 100px; }
  .dash-body { flex: 0 0 auto; padding: 12px 14px 8px; gap: 10px; min-height: 0; }
  .dash-card { padding: 14px; }
  .card-label { margin: -14px -14px 10px; padding: 8px 12px; font-size: 11px; }
  .summary-row { padding: 5px 0; }
  .row-key { font-size: 12px; }
  .row-val strong { font-size: 14px; }
  .goal-header { margin-bottom: 8px; }
  .progress-track { height: 6px; margin-bottom: 12px; }
  .add-money-btn { padding: 11px 0; font-size: 13px; width: 70%; }
  .actions-grid { gap: 8px; }
  .action-item { padding: 8px 4px; }
  .action-item:not(.action-item--loan) .action-icon-wrap { width: 50px; height: 50px; }
  .action-item:not(.action-item--loan) .action-icon-big { font-size: 24px; }
  .action-label { font-size: 9px; }
  .activity-row { padding: 6px 4px; }
  .activity-card { max-height: none; overflow: visible; }
  .footer-divider { margin: 12px 14px 6px; }
  .dash-footer { padding: 8px 0 12px; font-size: 10px; margin-top: 0; }
}

@media (max-width: 599px) and (max-height: 740px) {
  .dash-header { padding: 10px 12px 8px; }
  .welcome-title { font-size: 15px; }
  .header-divider { margin: 5px 0 3px; width: 80px; }
  .dash-body { padding: 10px 12px 6px; gap: 8px; }
  .dash-card { padding: 12px; }
  .card-label { margin: -12px -12px 8px; padding: 6px 10px; font-size: 10px; }
  .summary-row { padding: 4px 0; }
  .summary-row:first-of-type .row-val strong { font-size: 18px; }
  .goal-header { margin-bottom: 6px; }
  .progress-track { height: 5px; margin-bottom: 10px; }
  .add-money-btn { padding: 10px 0; font-size: 12px; }
  .actions-grid { gap: 6px; }
  .action-item { padding: 6px 3px; }
  .action-item:not(.action-item--loan) .action-icon-wrap { width: 44px; height: 44px; }
  .action-item:not(.action-item--loan) .action-icon-big { font-size: 20px; }
  .action-label { font-size: 8px; }
  .activity-row { padding: 5px 0; }
  .activity-amount { font-size: 12px; }
  .activity-desc { font-size: 11px; }
  .footer-divider { margin: 10px 12px 4px; }
  .dash-footer { padding: 6px 0 10px; font-size: 9.5px; }
}

@media (min-width: 600px) {
  .dashboard-page { max-width: 860px; }
  .dash-header, .dash-body, .dash-footer { padding-left: 20px; padding-right: 20px; }
  .actions-grid { grid-template-columns: repeat(auto-fit, minmax(110px, 1fr)); }
}

@media (min-width: 900px) {
  .dashboard-page { max-width: 1100px; }
  .dash-body {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
    padding-bottom: 12px;
  }
  .dash-card { height: 100%; }
}
</style>